import { Typography } from "@mui/material";
import { useState } from "react";
import TableBasedCrud, { ActionsMenuEntry } from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import OpsDateUtils from "../utils/OpsDateUtils";
import { AppDialog } from "../components/AppDialog";
import { AppToolbar } from "../components/AppToolbar";
import { ROWS_PER_PAGE } from "../App";
import { Accomplishment } from "../models/Accomplishment";
import { useMyAccomplishments } from "../hooks/useAccomplishments";
import AccomplishmentCreateModal from "../components/Accomplishment/AccomplishmentCreateModal";
import AccomplishmentUtils from "../utils/AccomplishmentUtils";
import { AccomplishmentStatusFilters } from "../utils/enums/filters/AccomplishmentStatusFilters";
import AccomplishmentEditFormModal from "../components/Accomplishment/AccomplishmentEditFormModal";
import AccomplishmentDetailsDialog from "../components/Accomplishment/AccomplishmentDetailsDialog";
import Assignation from "../models/Assignation";
import Position from "../models/Position";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { SpinnerWrapper } from "../components/SpinnerWrapper";
import useTableHeight from "../hooks/useTableHeight";
import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { useToolbarActions } from "../components/Main/ToolbarActions";
import { useStyles } from "../components/Main/formStyles";
import { Box } from "@mui/material";
import { Token } from "../theme";
import useAccomplishment from "../hooks/useAccomplishments";

export default function AccomplishmentSection() {
  const { toolbarActionsSlot, addButtonInToolbar } = useToolbarActions();
  const [selectedAccomplishment, setSelectedAccomplishment] = useState<Accomplishment>();
  const [links, setLinks] = useState<string[]>([]);
  const {
    onDataFetch,
    forceUpdateOn,
    totalPoints,
    isListLoading,
    isLoading,
    handleOnCreate: createAccomplishment,
    handleOnUpdate: updateAccomplishment,
    handleOnDelete,
  } = useMyAccomplishments();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [currentAccomplishment, setCurrentAccomplishment] = useState<Accomplishment | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [accomplishment, setAccomplishment] = useState<Accomplishment | null>();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();

  const customStyles = useStyles();

  const { accomplishmentTypes, getAccomplishmentData, userAssignations } = useAccomplishment({ loadFormData: true });

  const handleOnDeleteAccomplishment = () => {
    if (!selectedAccomplishment) return;
    handleOnDelete(selectedAccomplishment.externalId);
    setSelectedAccomplishment(undefined);
  };

  const getInfoProvided = (accomplishment: Accomplishment): string => {
    let linkCount: number = 0;
    if (accomplishment.info) linkCount = accomplishment.info.length;
    if (linkCount < 1) {
      return "(none)";
    }
    if (linkCount === 1) {
      return "1 entry";
    }
    return `${linkCount} entries`;
  };

  const handleViewDetails = (instance: Accomplishment) => {
    setAccomplishment(instance);
    setIsOpen(true);
  };

  const getStatusMessage = (accomplishment: Accomplishment): JSX.Element => {
    if (accomplishment.status === "Approved" && accomplishment.approvedDate) {
      let approveDate = OpsDateUtils.ensureFormatDateUTC(accomplishment.approvedDate);
      return (
        <>
          <span>{accomplishment.status}</span>
          <br></br>
          <span>{approveDate}</span>
        </>
      );
    }
    return <span>{accomplishment.status}</span>;
  };

  const getAditionalActions = (instance: Accomplishment, refresh: () => void): ActionsMenuEntry[] => {
    return [
      {
        title: "View Details",
        action: () => handleViewDetails(instance),
      },
      ...(isPending(instance)
        ? [
            {
              title: "Edit",
              action: () => {
                setLinks(instance.info || []);
                if (instance.assignation?.externalId === undefined) {
                  const emptyAssignation: Assignation = {
                    userFullName: "",
                    shortStartDate: new Date(),
                    shortEndDate: undefined,
                    position: {} as Position,
                    userExternalId: "",
                  };
                  emptyAssignation.externalId = "No role";
                  instance.assignation = emptyAssignation;
                }
                setCurrentAccomplishment(instance);
                setIsEdit(true);
              },
            },
            {
              title: "Delete",
              action: () => setSelectedAccomplishment(instance),
            },
          ]
        : []),
    ];
  };

  const handleOnCreate = (instance: Accomplishment) => {
    instance.info = links!;
    setLinks([]);
    return createAccomplishment(instance);
  };

  const handleOnUpdate = (instance: Accomplishment) => {
    instance.info = links || [];
    setLinks([]);
    setIsEdit(false);
    setCurrentAccomplishment(undefined);
    return updateAccomplishment(instance);
  };

  const isPending = (instance: Accomplishment) => {
    return instance.status === AccomplishmentStatusFilters.PENDING;
  };

  return (
    <AppContainerFullHeight
      bottomSectionRef={bottomSectionRef}
      topSection={
        <AppToolbar title={"My accomplishments"} rightContent={toolbarActionsSlot}>
          <Typography>{`Total points this month: ${totalPoints}`}</Typography>
        </AppToolbar>
      }
      bottomSection={
        <Box sx={{ borderRadius: 8 }}>
          {isListLoading && <SpinnerWrapper isLoading />}
          <Box sx={{ display: isListLoading ? "none" : "block" }}>
            <TableBasedCrud<Accomplishment>
              headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
              buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
              cancelButtonStyles={{
                color: Token.textSecondary,
                border: `1px solid ${Token.borderDefault}`,
                marginBottom: 10,
              }}
              dialogStyles={customStyles.root}
              buttonContainer={customButtonsContainer}
              gridButtonContainer={gridButtonContainer}
              addButtonBuilder={addButtonInToolbar(customButtonBuilder)}
              maxHeight={calculateMaxHeight(true)}
              stickyHeader
              stickyPagination
              elementName={"My accomplishments"}
              rowsPerPage={ROWS_PER_PAGE}
              onDataFetch={onDataFetch}
              onCreate={handleOnCreate}
              onCancelDialog={() => setLinks([])}
              forceUpdateOn={forceUpdateOn}
              denseTable
              disableAutoFetch={true}
              resetValue={{ type: "", assignationExternalId: "", positionExternalId: "", info: [] }}
              tableColumns={[
                {
                  header: "Type",
                  content: (instance) => <>{instance.type.title}</>,
                },
                {
                  header: "Performed On",
                  content: (instance) => <>{OpsDateUtils.ensureFormatDateUTC(instance.reportedOn)}</>,
                },
                {
                  header: "Entries",
                  content: (instance) => <>{getInfoProvided(instance)}</>,
                },
                {
                  header: "Status",
                  content: (instance) => (
                    <label
                      style={{
                        color: AccomplishmentUtils.getStatusColor(instance.status),
                      }}
                    >
                      {instance.status.replace("_", " ")}
                    </label>
                  ),
                },
                {
                  header: "Approved by",
                  content: (instance) => <>{instance.approver ? `${instance.approver.firstName} ${instance.approver.lastName}` : ""}</>,
                },
                {
                  header: "Approved on",
                  content: (instance) => <>{OpsDateUtils.ensureFormatDateUTC(instance.approvedDate)}</>,
                },
                {
                  header: "Points earned",
                  content: (instance) => <>{instance.points}</>,
                },
                {
                  header: "Role",
                  content: (instance) => <>{instance.role ? instance.role : "No role"}</>,
                },
              ]}
              buildAdditionalActions={getAditionalActions}
              renderDialogContent={(register, errors, control, instance) => (
                <AccomplishmentCreateModal
                  register={register}
                  errors={errors}
                  control={control}
                  isLoading={isLoading}
                  setLinks={setLinks}
                  links={links}
                  instance={instance!}
                  accomplishmentTypes={accomplishmentTypes}
                  userAssignations={userAssignations}
                  getAccomplishmentData={getAccomplishmentData}
                />
              )}
            />
          </Box>
          <AppDialog
            open={!!selectedAccomplishment}
            onClose={() => setSelectedAccomplishment(undefined)}
            title={"Delete"}
            content={
              <Typography>
                {`The accomplishment you selected is going to be eliminated.
            Do you want to confirm this action?`}
              </Typography>
            }
            buttons={[{ label: "Confirm delete", onClick: () => handleOnDeleteAccomplishment() }]}
          />

          <AccomplishmentEditFormModal
            isEdit={isEdit}
            setIsEdit={setIsEdit}
            currentAccomplishment={currentAccomplishment}
            setCurrentAccomplishment={setCurrentAccomplishment}
            handleOnUpdate={handleOnUpdate}
            links={links}
            setLinks={setLinks}
            isLoading={isLoading}
            accomplishmentTypes={accomplishmentTypes}
            userAssignations={userAssignations}
            getAccomplishmentData={getAccomplishmentData}
          />

          <AccomplishmentDetailsDialog isOpen={isOpen} setIsOpen={setIsOpen} accomplishment={accomplishment!} hideApproveButton={true} />
        </Box>
      }
    />
  );
}
